import { Component, input } from '@angular/core';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';

import { TranslationModule } from '@core/translation/translation.module';

import { U2bDatePickerApplyDirective } from '../directives/u2b-date-picker-apply.directive';
import { U2bDatePickerTimeInputComponent } from './u2b-date-picker-time-input.component';

/**
 * Button bar for a `<p-datepicker [showTime]>`, rendered through PrimeNG's
 * `#buttonbar` slot. It replaces the stock Heute/Löschen pair and adds
 * "Übernehmen", which commits a typed value and closes the panel — without it a
 * date typed without a time is discarded (see {@link U2bDatePickerApplyDirective}).
 * Above the buttons sits the typable clock that stands in for PrimeNG's read-only
 * spinner (see {@link U2bDatePickerTimeInputComponent}).
 *
 * PrimeNG supplies the surrounding `p-datepicker-buttonbar` element and styles it as
 * a flex row, so the host is the single stretched item in it and stacks the clock
 * above the button row. `(keydown)` mirrors every stock control in the panel: it is
 * the sole place PrimeNG handles Escape and the focus trap.
 */
@Component({
    selector: 'u2b-date-picker-button-bar',
    standalone: true,
    // Not OnPush: `showsTime()` reads the picker's own state, not an input of this
    // component, and `[showTime]` is bound dynamically where a service is billed hourly.
    imports: [Button, ButtonIcon, ButtonLabel, TranslationModule, U2bDatePickerTimeInputComponent],
    styles: [`
        :host {
            display: flex;
            flex: 1 1 auto;
            flex-direction: column;
            gap: 0.5rem;
        }

        .buttons {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.5rem;
        }
    `],
    template: `
        @if (picker().showsTime()) {
            <u2b-date-picker-time-input [picker]="picker()"/>
        }

        <div class="buttons">
            <p-button
                type="button"
                size="small"
                severity="secondary"
                variant="text"
                styleClass="p-datepicker-today-button"
                [label]="('common.datePicker.today' | translate)"
                (keydown)="picker().onButtonKeydown($event)"
                (onClick)="picker().today($event)"/>

            <p-button
                type="button"
                size="small"
                severity="secondary"
                variant="text"
                styleClass="p-datepicker-clear-button"
                [label]="('common.datePicker.clear' | translate)"
                (keydown)="picker().onButtonKeydown($event)"
                (onClick)="picker().clear($event)"/>

            <p-button
                type="button"
                size="small"
                (keydown)="picker().onButtonKeydown($event)"
                (onClick)="picker().apply()">
                <span pButtonIcon class="material-icons" style="font-size: 1.125rem">check</span>
                <span pButtonLabel>{{ 'common.datePicker.apply' | translate }}</span>
            </p-button>
        </div>
    `,
})
export class U2bDatePickerButtonBarComponent {

    readonly picker = input.required<U2bDatePickerApplyDirective>();
}
